import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import InternalNavbar from '../../components/InternalNavbar';
import Footer from '../../components/footer';
import Scrollbar from '../../components/scrollbar';
import LevelCard from '../../components/LevelCard';
import { FaArrowLeft, FaExclamationTriangle, FaSpinner } from 'react-icons/fa';
import courseService from '../../services/courseService';
import arabicBg from '../../images/arabic-bg-dark.svg';
import './style.css';

// Level codes in order - moved outside component for stability
const LEVEL_CODES = ['A0', 'A1', 'A2', 'A3', 'B1', 'B2', 'B3'];

const LevelsPage = () => {
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAllLevels = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch all levels from the API
        const allLevels = await courseService.getAllCourses();
        
        // Make sure allLevels is an array before processing
        const safeAllLevels = Array.isArray(allLevels) ? allLevels : [];
        
        // Manual reordering to ensure proper sequence (A0 first, then A1.1, A1.2, etc.)
        const sortedLevels = [];
        
        // Add levels in the exact order we want
        LEVEL_CODES.forEach(levelCode => {
          const level = safeAllLevels.find(l => (l.level || '').trim() === levelCode);
          if (level) {
            sortedLevels.push(level);
          }
        });
        
        // Add any remaining levels that don't match our predefined codes
        safeAllLevels.forEach(level => {
          const levelCode = (level.level || '').trim();
          if (!LEVEL_CODES.includes(levelCode) && !sortedLevels.includes(level)) {
            sortedLevels.push(level);
          }
        });
        
        setLevels(sortedLevels);
      } catch (err) {
        console.error('Error fetching levels:', err);
        setError('Failed to load levels. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchAllLevels();
  }, []);

  // Loading state
  if (loading) {
    return (
      <div className="levels-page">
        <InternalNavbar />
        <div className="levels-container">
          <div className="levels-wrapper">
            <div className="page-header">
              <Link to="/dashboard" className="back-link">
                <FaArrowLeft /> Back to Dashboard
              </Link>
            </div>
            
            <div className="loading-state">
              <div className="loading-content">
                <FaSpinner className="loading-spinner" />
                <h2>Loading Levels...</h2>
                <p>Please wait while we fetch the latest level information.</p>
              </div>
            </div>
          </div>
        </div>
        <Footer />
        <Scrollbar />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="levels-page">
        <InternalNavbar />
        <div className="levels-container">
          <div className="levels-wrapper">
            <div className="page-header">
              <Link to="/dashboard" className="back-link">
                <FaArrowLeft /> Back to Dashboard
              </Link>
            </div>
            
            <div className="error-state">
              <div className="error-content">
                <FaExclamationTriangle className="error-icon" />
                <h2>Unable to Load Levels</h2>
                <p>{error}</p>
                <button 
                  onClick={() => window.location.reload()} 
                  className="retry-btn"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
        <Footer />
        <Scrollbar />
      </div>
    );
  }

  return (
    <div className="levels-page">
      <InternalNavbar />
      
      {/* Arabic Background */}
      <div 
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url(${arabicBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          opacity: 0.05,
          zIndex: 0
        }}
      />
      
      <div className="levels-container" style={{ position: 'relative', zIndex: 1 }}>
        <div className="levels-wrapper">
          {/* Page Header */}
          <div className="page-header">
            <Link to="/dashboard" className="back-link">
              <FaArrowLeft /> Back to Dashboard
            </Link>
          </div>

          {/* Page Title Section */}
          <div className="page-title-section">
            <div className="title-content">
              <span className="title-badge">LEARNING LEVELS</span>
              <h1>Master Arabic Step by Step</h1>
              <p className="page-subtitle">
                Progress through our structured Arabic learning curriculum, from absolute beginner to intermediate level. 
                Each level builds systematically on the previous one, ensuring solid foundations and practical language skills.
              </p>
            </div>
          </div>

          {/* Levels Grid */}
          <div className="levels-grid">
            {levels.length > 0 ? (
              levels.map((level) => (
                <LevelCard 
                  key={level.id || level.level} 
                  level={level} 
                />
              ))
            ) : (
              <div className="no-levels">
                <h3>No Levels Available</h3>
                <p>Levels are currently being prepared. Please check back later.</p>
              </div>
            )}
          </div>

          {/* Additional Information */}
          <div className="levels-info">
            <div className="info-card">
              <h3>About Our Curriculum</h3>
              <p>
                Our Arabic language levels follow the Common European Framework of Reference for Languages (CEFR) 
                standards, ensuring a structured and internationally recognized learning path. Each level builds 
                systematically on the previous one, incorporating modern teaching methodologies and interactive content.
              </p>
            </div>
            
            <div className="info-card">
              <h3>Learning Approach</h3>
              <p>
                We combine traditional Arabic language teaching with modern digital learning techniques. 
                Our levels include interactive exercises, multimedia content, cultural insights, and practical 
                conversation practice to ensure comprehensive language acquisition.
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
      <Scrollbar />
    </div>
  );
};

export default LevelsPage;